import { SseMessage } from "./sse.types";

const FRAME_SEPARATOR = /\r\n\r\n|\n\n|\r\r/;
const LINE_SEPARATOR = /\r\n|\n|\r/;

/**
 * Incremental decoder for the text/event-stream wire format.
 *
 * Deliberately free of Angular and DOM dependencies so the parsing rules can be
 * exercised on their own — network chunks split frames at arbitrary points, so
 * this is the part most worth testing.
 *
 * Usage: feed decoded text with `push()`, which returns whichever events became
 * complete. Partial frames stay buffered until their terminating blank line.
 */
export class SseFrameDecoder {
    private buffer = "";

    /** Last `id:` seen — resume point for `Last-Event-ID` on reconnect. */
    lastEventId: string | null = null;

    /** Last `retry:` seen, in ms. */
    retryMs: number | null = null;

    push(chunk: string): SseMessage[] {
        this.buffer += chunk;

        const messages: SseMessage[] = [];
        let match = FRAME_SEPARATOR.exec(this.buffer);

        while (match) {
            const frame = this.buffer.slice(0, match.index);
            this.buffer = this.buffer.slice(match.index + match[0].length);

            const message = this.decodeFrame(frame);
            if (message) {
                messages.push(message);
            }
            match = FRAME_SEPARATOR.exec(this.buffer);
        }

        return messages;
    }

    /** Drops any half-received frame — call before reconnecting. */
    reset(): void {
        this.buffer = "";
    }

    private decodeFrame(frame: string): SseMessage | null {
        if (!frame.trim()) {
            return null;
        }

        let event = "message";
        let id: string | null = null;
        const dataLines: string[] = [];

        for (const rawLine of frame.split(LINE_SEPARATOR)) {
            // ":" starts a comment — servers use these as keep-alive pings.
            if (!rawLine || rawLine.startsWith(":")) {
                continue;
            }

            const colon = rawLine.indexOf(":");
            const field = colon === -1 ? rawLine : rawLine.slice(0, colon);
            let value = colon === -1 ? "" : rawLine.slice(colon + 1);
            if (value.startsWith(" ")) {
                value = value.slice(1);
            }

            switch (field) {
                case "event":
                    event = value;
                    break;
                case "data":
                    dataLines.push(value);
                    break;
                case "id":
                    id = value;
                    this.lastEventId = value;
                    break;
                case "retry": {
                    const ms = parseInt(value, 10);
                    if (!isNaN(ms)) {
                        this.retryMs = ms;
                    }
                    break;
                }
                default:
                    // Unknown fields are ignored, per the spec.
                    break;
            }
        }

        // A frame with no data field dispatches nothing.
        if (!dataLines.length) {
            return null;
        }

        return { event, data: dataLines.join("\n"), id };
    }
}
